import React, { useState, useMemo, useEffect } from 'react';
import { Row, Col, FormGroup } from 'react-bootstrap';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';
import { MultiSelect, Option } from 'react-multi-select-component';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import cloneDeep from 'lodash/cloneDeep';
import {
  createOptions,
  checkArray,
  monthsPlaceholder,
  getYears,
  converDropdownDataFilterData,
} from '../../../../../Components/helper';
import CustomTooltip from '../../../../../Components/Tooltip/CustomTooltip';
import * as systemMasterAction from '../../../../SystemMasters/Store/SystemMasters.action';
import * as commonAction from '../../TodoCommon.action';
import { CurrentYearItemRenderer } from '../../../../../Components/Utility/CurrentYearDropdown';

export interface MyTodoFilterState {
  activeStatus: string[];
  activeBrand: string[];
  activeProductType: string[];
  activeCat1: string[];
  activeCat2: string[];
  activeCat3: string[];
  activeCat4: string[];
  activeSupplier: string[];
  activeSeason: string[];
  activeMonth: string[];
  activeYear: string[];
  activeCreatedBy: string[];
  activeLicensor: string[];
  activeLicense: string[];
  activeLikeness: string[];
  activeSubBrand: string[];
  activeColorway: string[];
  activeColorGroup: string[];
  productStatus: string[];
  dynamicTypes: Array<{ colId: string; valId: string }>;
  textCustomAttribute: string[];
}

export interface MyTodoApplyFilterPayload {
  status?: string[];
  brandId?: string[];
  subBrandId?: string[];
  productType?: string[];
  catOneId?: string[];
  catTwoId?: string[];
  catThreeId?: string[];
  catFourId?: string[];
  supplierId?: string[];
  seasonId?: string[];
  year?: string[];
  month?: string[];
  createdBy?: string[];
  productStatus?: string[];
  licensorId?: string[];
  licenseId?: string[];
  likenessId?: string[];
  colorwayId?: string[];
  colorGroupId?: string[];
  dynamicTypes?: Array<{ colId: string; valId: string }>;
  textCustomAttribute?: string[];
}

const initialFilterState: MyTodoFilterState = {
  activeStatus: [],
  activeBrand: [],
  activeProductType: [],
  activeCat1: [],
  activeCat2: [],
  activeCat3: [],
  activeCat4: [],
  activeSupplier: [],
  activeSeason: [],
  activeMonth: [],
  activeYear: [],
  activeCreatedBy: [],
  activeLicensor: [],
  activeLicense: [],
  activeLikeness: [],
  activeSubBrand: [],
  activeColorway: [],
  activeColorGroup: [],
  productStatus: [],
  dynamicTypes: [],
  textCustomAttribute: [],
};

interface DynamicField {
  _id: string;
  keyName: string;
  type: string;
  isActive?: boolean;
  brand?: string[];
  productType?: string[];
  values?: Array<{ _id: string; keyName: string; isActive?: boolean }>;
}

interface MyTodoFilterProps {
  applyFilter: (filterObj: MyTodoApplyFilterPayload) => void;
  filterState: MyTodoFilterState;
  setFilterState: React.Dispatch<React.SetStateAction<MyTodoFilterState>>;
}

const MyTodoFilter: React.FC<MyTodoFilterProps> = ({
  applyFilter,
  filterState,
  setFilterState,
}) => {
  const { t } = useTranslation('modules/my-todo');
  const dispatch = useDispatch();
  const reducer = useSelector((state: Record<string, unknown>) => state);
  const {
    UserDetails,
    SystemMastersDetails,
    BomLibraryDetails,
    Measuremets,
    ColorLibraryDetails,
  } = reducer as {
    UserDetails: { userInfo?: { userId?: string }; isSupplierLoggedIn?: boolean; isCompanyUserLoggedIn?: boolean };
    SystemMastersDetails: {
      brandList?: Array<{ _id: string; name: string }>;
      subBrandList?: Array<{ _id: string; name: string; brandId?: string }>;
      category1List?: Array<{ _id: string; name: string }>;
      category2List?: Array<{ _id: string; name: string }>;
      category3List?: Array<{ _id: string; name: string }>;
      category4List?: Array<{ _id: string; name: string }>;
      pcTypeList?: Array<{ _id: string; name: string }>;
      productStatusList?: Array<{ _id: string; name: string }>;
      seasonList?: Array<{ _id: string; name: string }>;
      licensorList?: Array<{ _id: string; name: string }>;
      licenseList?: Array<{ _id: string; name: string }>;
      likenessList?: Array<{ _id: string; name: string }>;
      subBrandAccess?: boolean;
      customAttributeList?: Array<{
        _id: string;
        keyName: string;
        type: string;
        isLable: boolean;
        lableId?: string;
        isActive?: boolean;
        brand?: string[];
        productType?: string[];
        values?: Array<{ _id: string; keyName: string; isActive?: boolean }>;
      }>;
      pcpCustomAttribute?: Array<{
        _id: string;
        keyName: string;
        isLable: boolean;
        lableId?: string;
        isActive?: boolean;
        productType?: string[];
        values?: Array<{ _id: string; keyName: string; isActive?: boolean }>;
      }>;
    };
    BomLibraryDetails: { ReferenceSupplier?: Array<{ _id: string; name: string }> };
    Measuremets: {
      adminUsers?: Array<{ _id: string; firstName?: string; lastName?: string }>;
      companyUsers?: Array<{ _id: string; firstName?: string; lastName?: string }>;
    };
    ColorLibraryDetails: {
      ColorWaysLibList?: Array<{ _id: string; refName?: string }>;
      ColorLibGroup?: Array<{ _id: string; name: string }>;
    };
  };

  const { userInfo, isSupplierLoggedIn, isCompanyUserLoggedIn } = UserDetails ?? {};
  const { adminUsers = [], companyUsers = [] } = Measuremets ?? {};
  const { ReferenceSupplier = [] } = BomLibraryDetails ?? {};
  const {
    brandList = [],
    subBrandList = [],
    category1List = [],
    category2List = [],
    category3List = [],
    category4List = [],
    pcTypeList = [],
    productStatusList = [],
    seasonList = [],
    licensorList = [],
    licenseList = [],
    likenessList = [],
    subBrandAccess = false,
    customAttributeList = [],
    pcpCustomAttribute = [],
  } = SystemMastersDetails ?? {};
  const { ColorWaysLibList = [], ColorLibGroup: colorLibList = [] } = ColorLibraryDetails ?? {};

  const [userSuppliers, setUserSuppliers] = useState<Array<{ _id: string; name: string }>>([]);

  const {
    activeStatus = [],
    activeBrand = [],
    activeProductType = [],
    activeCat1 = [],
    activeCat2 = [],
    activeCat3 = [],
    activeCat4 = [],
    activeSupplier = [],
    activeSeason = [],
    activeMonth = [],
    activeYear = [],
    activeCreatedBy = [],
    activeLicensor = [],
    activeLicense = [],
    activeLikeness = [],
    activeSubBrand = [],
    activeColorway = [],
    activeColorGroup = [],
    productStatus = [],
    dynamicTypes = [],
    textCustomAttribute = [],
  } = filterState;

  const isApply = useMemo(
    () =>
      activeStatus.length > 0 ||
      activeBrand.length > 0 ||
      activeSubBrand.length > 0 ||
      activeProductType.length > 0 ||
      activeCat1.length > 0 ||
      activeCat2.length > 0 ||
      activeCat3.length > 0 ||
      activeCat4.length > 0 ||
      activeSupplier.length > 0 ||
      activeSeason.length > 0 ||
      activeMonth.length > 0 ||
      activeYear.length > 0 ||
      activeCreatedBy.length > 0 ||
      activeLicensor.length > 0 ||
      activeLicense.length > 0 ||
      activeLikeness.length > 0 ||
      activeColorway.length > 0 ||
      activeColorGroup.length > 0 ||
      productStatus.length > 0 ||
      dynamicTypes.length > 0 ||
      textCustomAttribute.length > 0,
    [
      activeStatus, activeBrand, activeSubBrand, activeProductType,
      activeCat1, activeCat2, activeCat3, activeCat4,
      activeSupplier, activeSeason, activeMonth, activeYear,
      activeCreatedBy, activeLicensor, activeLicense, activeLikeness,
      activeColorway, activeColorGroup, productStatus, dynamicTypes, textCustomAttribute,
    ],
  );

  const monthOptions = useMemo(
    () => createOptions(monthsPlaceholder, 'name', '_id') as Option[],
    [],
  );
  const yearOptions = useMemo(() => createOptions(getYears(), 'name', '_id') as Option[], []);

  const monthYearBasedOptions = useMemo((): Option[] => {
    const months = monthOptions;
    const years = (activeYear || []).slice().sort((a, b) => Number(a) - Number(b));
    if (months.length > 0 && years.length > 0) {
      const combined: Option[] = [];
      months.forEach((month: Option) => {
        years.forEach((year: string) => {
          const val = `${month.value ?? ''}_${year}`;
          combined.push({
            ...month,
            value: val,
            label: `${month.label ?? ''} ${year}`,
          });
        });
      });
      return combined;
    }
    return monthOptions;
  }, [activeYear, monthOptions]);

  useEffect(() => {
    /**
     * Single aggregated call replaces ~14 parallel master/colour requests.
     * The thunk fans the response into the same reducers those calls populated.
     */
    if (!userInfo?.userId) return;
    dispatch(
      commonAction.loadMyTodoFilterMasters({ loggedInUserId: userInfo.userId }) as never,
    );
  }, [dispatch, userInfo?.userId]);

  useEffect(() => {
    if (checkArray(activeCat2) && checkArray(activeCat1) && userInfo?.userId) {
      dispatch(
        systemMasterAction.getCategory3Details({
          categoryOneId: activeCat1,
          categoryTwoId: activeCat2,
          categoryType: 3,
          loggedInUserId: userInfo.userId,
        }) as never,
      );
    }
  }, [activeCat2, activeCat1, dispatch, userInfo?.userId]);

  useEffect(() => {
    if (
      checkArray(activeCat2) &&
      checkArray(activeCat1) &&
      userInfo?.userId &&
      checkArray(activeCat3)
    ) {
      dispatch(
        systemMasterAction.getCategory4Details({
          categoryOneId: activeCat1,
          categoryTwoId: activeCat2,
          categoryThreeId: activeCat3,
          categoryType: 4,
          loggedInUserId: userInfo.userId,
        }) as never,
      );
    }
  }, [activeCat2, activeCat1, activeCat3, dispatch, userInfo?.userId]);

  useEffect(() => {
    if (checkArray(activeBrand) && userInfo?.userId) {
      dispatch(
        systemMasterAction.getSubBrandDetails({
          brandId: activeBrand,
          loggedInUserId: userInfo.userId,
          isActive: true,
        }) as never,
      );
    }
  }, [activeBrand, dispatch, userInfo?.userId]);

  useEffect(() => {
    const fetchUserSuppliers = async () => {
      if (isCompanyUserLoggedIn) {
        const data = await dispatch(commonAction.getUserSuppliers() as never);
        const res = data as { suppliers?: Array<{ _id: string; name?: string; companies?: Array<{ supplierName?: string }> }> };
        const supplist =
          res?.suppliers?.length
            ? res.suppliers.map((ele) => ({
                _id: ele._id,
                name: ele.companies?.[0]?.supplierName ?? ele.name ?? '',
              }))
            : [];
        setUserSuppliers(supplist);
      }
    };
    fetchUserSuppliers();
  }, [isCompanyUserLoggedIn, dispatch]);

  const handleFilterLicense = (
    val: string[],
    key: 'activeLicensor' | 'activeLicense' | 'activeLikeness',
  ) => {
    setFilterState((prev) => {
      const newState = { ...prev, [key]: val };
      if (key === 'activeLicensor') {
        newState.activeLicense = [];
        newState.activeLikeness = [];
        if (val?.length > 0 && userInfo?.userId) {
          dispatch(
            systemMasterAction.getLicenseDetails({
              licensorIds: val,
              loggedInUserId: userInfo.userId,
              isActive: true,
            }) as never,
          );
        }
        dispatch(systemMasterAction.clearLicenseData() as never);
        dispatch(systemMasterAction.clearLikenessData() as never);
      }
      if (key === 'activeLicense') {
        newState.activeLikeness = [];
        if (val?.length > 0 && checkArray(activeLicensor) && userInfo?.userId) {
          dispatch(
            systemMasterAction.getLikenessDetails({
              licensorIds: activeLicensor,
              licenseIds: val,
              loggedInUserId: userInfo.userId,
              isActive: true,
            }) as never,
          );
        }
        dispatch(systemMasterAction.clearLikenessData() as never);
      }
      return newState;
    });
  };

  const handleFilterSelect = (val: string[], key: keyof MyTodoFilterState) => {
    setFilterState((prev) => ({
      ...prev,
      [key]: val,
    }));
  };

  const handleFilterApply = () => {
    const filterObj: MyTodoApplyFilterPayload = {
      status: activeStatus,
      brandId: activeBrand,
      subBrandId: activeSubBrand,
      productType: activeProductType,
      catOneId: activeCat1,
      catTwoId: activeCat2,
      catThreeId: activeCat3,
      catFourId: activeCat4,
      supplierId: activeSupplier,
      seasonId: activeSeason,
      month: activeMonth,
      year: activeYear.map((y) => String(y)),
      createdBy: activeCreatedBy,
      productStatus,
      licensorId: activeLicensor,
      licenseId: activeLicense,
      likenessId: activeLikeness,
      colorwayId: activeColorway,
      colorGroupId: activeColorGroup,
      dynamicTypes,
      textCustomAttribute,
    };
    applyFilter(filterObj);
  };

  const handleFilterClear = () => {
    setFilterState(initialFilterState);
    applyFilter({
      status: [],
      brandId: [],
      subBrandId: [],
      productType: [],
      catOneId: [],
      catTwoId: [],
      catThreeId: [],
      catFourId: [],
      supplierId: [],
      seasonId: [],
      month: [],
      year: [],
      createdBy: [],
      productStatus: [],
      licensorId: [],
      licenseId: [],
      likenessId: [],
      colorwayId: [],
      colorGroupId: [],
      dynamicTypes: [],
      textCustomAttribute: [],
    });
  };

  const brandOptions = useMemo(
    () => (checkArray(brandList) ? createOptions(brandList, 'name', '_id') as Option[] : []),
    [brandList],
  );
  const subBrandOptions = useMemo(
    () => (checkArray(subBrandList) ? createOptions(subBrandList, 'name', '_id') as Option[] : []),
    [subBrandList],
  );
  const category1Options = useMemo(
    () => (checkArray(category1List) ? createOptions(category1List, 'name', '_id') as Option[] : []),
    [category1List],
  );
  const category2Options = useMemo(
    () => (checkArray(category2List) ? createOptions(category2List, 'name', '_id') as Option[] : []),
    [category2List],
  );
  const category3Options = useMemo(
    () => (checkArray(category3List) ? createOptions(category3List, 'name', '_id') as Option[] : []),
    [category3List],
  );
  const category4Options = useMemo(
    () => (checkArray(category4List) ? createOptions(category4List, 'name', '_id') as Option[] : []),
    [category4List],
  );
  const productTypeOptions = useMemo(
    () => (checkArray(pcTypeList) ? createOptions(pcTypeList, 'name', '_id') as Option[] : []),
    [pcTypeList],
  );
  const productStatusOptions = useMemo(
    () =>
      checkArray(productStatusList)
        ? createOptions(productStatusList, 'name', '_id') as Option[]
        : [],
    [productStatusList],
  );
  const seasonOptions = useMemo(
    () => (checkArray(seasonList) ? createOptions(seasonList, 'name', '_id') as Option[] : []),
    [seasonList],
  );
  const licensorOptions = useMemo(
    () => (checkArray(licensorList) ? createOptions(licensorList, 'name', '_id') as Option[] : []),
    [licensorList],
  );
  const licenseOptions = useMemo(
    () => (checkArray(licenseList) ? createOptions(licenseList, 'name', '_id') as Option[] : []),
    [licenseList],
  );
  const likenessOptions = useMemo(
    () => (checkArray(likenessList) ? createOptions(likenessList, 'name', '_id') as Option[] : []),
    [likenessList],
  );
  const colorwayOptions = useMemo(
    () =>
      checkArray(ColorWaysLibList)
        ? createOptions(ColorWaysLibList, 'refName', '_id') as Option[]
        : [],
    [ColorWaysLibList],
  );
  const colorGroupOptions = useMemo(
    () => (checkArray(colorLibList) ? createOptions(colorLibList, 'name', '_id') as Option[] : []),
    [colorLibList],
  );

  const dynamicFields = useMemo(
    () => (checkArray(customAttributeList) ? converDropdownDataFilterData(cloneDeep(customAttributeList)) as DynamicField[] : [] as DynamicField[]),
    [customAttributeList],
  );

  const multiDropdownFields = useMemo(() => {
    if (!checkArray(pcpCustomAttribute)) return [];
    return pcpCustomAttribute.filter((ele) => ele.isActive !== false);
  }, [pcpCustomAttribute]);
  

  const TextCustomAttributeOptions = useMemo((): Option[] => {
    if (!checkArray(customAttributeList)) return [];
    return customAttributeList
      .filter((ele) => ele.type === 'text' && ele.isLable === false)
      .map((e) => ({ label: e.keyName, value: e._id }));
  }, [customAttributeList]);

  useEffect(() => {
    const brandIds = Array.isArray(activeBrand) ? activeBrand : [];
    const productTypeIds = Array.isArray(activeProductType) ? activeProductType : [];
    if (brandIds.length === 0 && productTypeIds.length === 0) return;

    type DropdownField = { _id: string; brand?: string[]; productType?: string[] };
    const validDropdownIds = (dynamicFields as DropdownField[])
      .filter((field) => {
        const matchesBrand = field?.brand?.length ? field.brand.some((br) => brandIds.includes(br)) : false;
        const matchesProductType = field?.productType?.length ? field.productType.some((pt) => productTypeIds.includes(pt)) : false;
        return brandIds.length > 0 && productTypeIds.length > 0 ? matchesBrand && matchesProductType : true;
      })
      .map((f) => f._id);

    type MultiField = { _id: string; productType?: string[] };
    const validMultiIds = (multiDropdownFields as MultiField[])
      .filter((field) => {
        if (!productTypeIds.length) return true;
        return field?.productType?.length ? field.productType.some((pt) => productTypeIds.includes(pt)) : true;
      })
      .map((f) => f._id);

    const validColIds = [...validDropdownIds, ...validMultiIds];
    if (!checkArray(validColIds)) return;
    setFilterState((prev) => ({
      ...prev,
      dynamicTypes: prev.dynamicTypes.filter((d) => validColIds.includes(d.colId)),
    }));
  }, [activeBrand, activeProductType, dynamicFields, multiDropdownFields]);

  const handleFilterDynamicSelect = (items: string[], colId: string) => {
    const existing = dynamicTypes.filter((e) => e.colId === colId);
    const copy = cloneDeep(dynamicTypes);
    if (checkArray(existing)) {
      existing.forEach((ele) => {
        const idx = copy.findIndex((e: { colId: string; valId: string }) => e.valId === ele.valId);
        if (idx !== -1) copy.splice(idx, 1);
      });
    }
    const newEntries = checkArray(items) ? items.map((valId) => ({ colId, valId })) : [];
    setFilterState((prev) => ({
      ...prev,
      dynamicTypes: [...copy, ...newEntries],
    }));
  };

  const supplierOptions = useMemo(() => {
    let options: Array<{ label: string; value: string }> = [];
    if (checkArray(ReferenceSupplier) && !isCompanyUserLoggedIn) {
      options = createOptions(ReferenceSupplier, 'name', '_id');
    } else if (checkArray(userSuppliers) && isCompanyUserLoggedIn) {
      options = createOptions(userSuppliers, 'name', '_id');
    }
    return options?.sort((a, b) =>
      (a?.label ?? '').trim().localeCompare((b?.label ?? '').trim(), undefined, {
        sensitivity: 'base',
      }),
    );
  }, [ReferenceSupplier, isCompanyUserLoggedIn, userSuppliers]);

  const createdByOption = useMemo((): Option[] => {
    const allUser = [...(Array.isArray(adminUsers) ? adminUsers : []), ...(Array.isArray(companyUsers) ? companyUsers : [])];
    if (checkArray(allUser)) {
      return allUser.map((ele) => ({
        label: `${ele?.firstName ?? ''} ${ele?.lastName ?? ''}`.trim(),
        value: ele?._id,
      }));
    }
    return [];
  }, [adminUsers, companyUsers]);

  const statusList = useMemo(
    () => [
      { _id: 'Pending', name: t('MY_TODO.STATUS.PENDING') },
      { _id: 'Done', name: t('MY_TODO.STATUS.DONE') },
    ],
    [t],
  );
  const statusOptions = useMemo(
    () => (checkArray(statusList) ? createOptions(statusList, 'name', '_id') as Option[] : []),
    [statusList],
  );

  return (
    <CustomTooltip placement="top" tooltipText={t('MY_TODO.FILTER.ARIA_LABEL')}>
      <button
        type="button"
        aria-label={t('MY_TODO.FILTER.ARIA_LABEL')}
        className={isApply ? 'btn d-inline-flex p-0 active' : 'btn d-inline-flex p-0'}
      >
        <OverlayTrigger
          placement="bottom"
          trigger="click"
          rootClose
          overlay={
            <Popover id="mytodo-filter-popover" className="custom-popover popover-filter">
              <Popover.Content>
                <Row className="form-row">
                  <Col md={2}>
                    <FormGroup>
                      <MultiSelect
                        options={statusOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={
                          statusOptions?.filter(
                            (val) =>
                              checkArray(statusOptions) && activeStatus.includes(val.value),
                          ) ?? []
                        }
                        labelledBy={t('MY_TODO.FILTER.SELECT_STATUS')}
                        overrideStrings={{ selectSomeItems: t('MY_TODO.FILTER.SELECT_STATUS') }}
                        onChange={(selected: Option[]) =>
                          handleFilterSelect(
                            selected.map((v) => v.value),
                            'activeStatus',
                          )
                        }
                      />
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <MultiSelect
                        options={brandOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={
                          brandOptions?.filter(
                            (val) => checkArray(brandOptions) && activeBrand.includes(val.value),
                          ) ?? []
                        }
                        labelledBy={t('MY_TODO.FILTER.SELECT_BRAND')}
                        overrideStrings={{ selectSomeItems: t('MY_TODO.FILTER.SELECT_BRAND') }}
                        onChange={(selected: Option[]) =>
                          handleFilterSelect(selected.map((v) => v.value), 'activeBrand')
                        }
                      />
                    </FormGroup>
                  </Col>
                  {subBrandAccess && (
                    <Col md={2}>
                      <FormGroup>
                        <MultiSelect
                          options={subBrandOptions}
                          className="product-custom-multiselect common-new-multiselect w-100"
                          value={
                            subBrandOptions?.filter(
                              (val) =>
                                checkArray(subBrandOptions) &&
                                activeSubBrand.includes(val.value),
                            ) ?? []
                          }
                          labelledBy={t('MY_TODO.FILTER.SELECT_SUB_BRAND')}
                          overrideStrings={{ selectSomeItems: t('MY_TODO.FILTER.SELECT_SUB_BRAND') }}
                          onChange={(selected: Option[]) =>
                            handleFilterSelect(selected.map((v) => v.value), 'activeSubBrand')
                          }
                        />
                      </FormGroup>
                    </Col>
                  )}
                  <Col md={2}>
                    <FormGroup>
                      <MultiSelect
                        options={productTypeOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={
                          productTypeOptions?.filter(
                            (val) =>
                              checkArray(productTypeOptions) &&
                              activeProductType.includes(val.value),
                          ) ?? []
                        }
                        labelledBy={t('MY_TODO.FILTER.SELECT_PRODUCT_TYPE')}
                        overrideStrings={{ selectSomeItems: t('MY_TODO.FILTER.SELECT_PRODUCT_TYPE') }}
                        onChange={(selected: Option[]) =>
                          handleFilterSelect(
                            selected.map((v) => v.value),
                            'activeProductType',
                          )
                        }
                      />
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <MultiSelect
                        options={category1Options}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={
                          category1Options?.filter(
                            (val) =>
                              checkArray(category1Options) && activeCat1.includes(val.value),
                          ) ?? []
                        }
                        labelledBy={t('MY_TODO.FILTER.SELECT_CATEGORY_ONE')}
                        overrideStrings={{ selectSomeItems: t('MY_TODO.FILTER.SELECT_CATEGORY_ONE') }}
                        onChange={(selected: Option[]) =>
                          handleFilterSelect(selected.map((v) => v.value), 'activeCat1')
                        }
                      />
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <MultiSelect
                        options={category2Options}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={
                          category2Options?.filter(
                            (val) =>
                              checkArray(category2Options) && activeCat2.includes(val.value),
                          ) ?? []
                        }
                        labelledBy={t('MY_TODO.FILTER.SELECT_CATEGORY_TWO')}
                        overrideStrings={{ selectSomeItems: t('MY_TODO.FILTER.SELECT_CATEGORY_TWO') }}
                        onChange={(selected: Option[]) =>
                          handleFilterSelect(selected.map((v) => v.value), 'activeCat2')
                        }
                      />
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <MultiSelect
                        options={category3Options}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={
                          category3Options?.filter(
                            (val) =>
                              checkArray(category3Options) && activeCat3.includes(val.value),
                          ) ?? []
                        }
                        labelledBy={t('MY_TODO.FILTER.SELECT_CATEGORY_THREE')}
                        overrideStrings={{ selectSomeItems: t('MY_TODO.FILTER.SELECT_CATEGORY_THREE') }}
                        onChange={(selected: Option[]) =>
                          handleFilterSelect(selected.map((v) => v.value), 'activeCat3')
                        }
                      />
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <MultiSelect
                        options={category4Options}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={
                          category4Options?.filter(
                            (val) =>
                              checkArray(category4Options) && activeCat4.includes(val.value),
                          ) ?? []
                        }
                        labelledBy={t('MY_TODO.FILTER.SELECT_CATEGORY_FOUR')}
                        overrideStrings={{ selectSomeItems: t('MY_TODO.FILTER.SELECT_CATEGORY_FOUR') }}
                        onChange={(selected: Option[]) =>
                          handleFilterSelect(selected.map((v) => v.value), 'activeCat4')
                        }
                      />
                    </FormGroup>
                  </Col>
                  {!isSupplierLoggedIn && (
                    <Col md={2}>
                      <FormGroup>
                        <MultiSelect
                          options={supplierOptions}
                          className="product-custom-multiselect common-new-multiselect w-100"
                          value={
                            supplierOptions?.filter(
                              (val) =>
                                checkArray(supplierOptions) &&
                                activeSupplier.includes(val.value),
                            ) ?? []
                          }
                          labelledBy={t('MY_TODO.FILTER.SELECT_SUPPLIER')}
                          overrideStrings={{
                            selectSomeItems: t('MY_TODO.FILTER.SELECT_SUPPLIER'),
                          }}
                          onChange={(selected: Option[]) =>
                            handleFilterSelect(selected.map((v) => v.value), 'activeSupplier')
                          }
                        />
                      </FormGroup>
                    </Col>
                  )}
                  <Col md={2}>
                    <FormGroup>
                      <MultiSelect
                        options={seasonOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={
                          seasonOptions?.filter(
                            (val) =>
                              checkArray(seasonOptions) && activeSeason.includes(val.value),
                          ) ?? []
                        }
                        labelledBy={t('MY_TODO.FILTER.SELECT_SEASON')}
                        overrideStrings={{
                          selectSomeItems: t('MY_TODO.FILTER.SELECT_SEASON'),
                        }}
                        onChange={(selected: Option[]) =>
                          handleFilterSelect(selected.map((v) => v.value), 'activeSeason')
                        }
                      />
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <MultiSelect
                        ItemRenderer={CurrentYearItemRenderer}
                        options={yearOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={
                          yearOptions?.filter(
                            (val) =>
                              checkArray(yearOptions) &&
                              activeYear.includes(val.value?.toString()),
                          ) ?? []
                        }
                        labelledBy={t('MY_TODO.FILTER.SELECT_YEAR')}
                        overrideStrings={{
                          selectSomeItems: t('MY_TODO.FILTER.SELECT_YEAR'),
                        }}
                        onChange={(selected: Option[]) =>
                          handleFilterSelect(
                            selected.map((v) => v.value?.toString()),
                            'activeYear',
                          )
                        }
                      />
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <MultiSelect
                        options={monthYearBasedOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={
                          monthYearBasedOptions?.filter(
                            (val) =>
                              checkArray(monthYearBasedOptions) &&
                              activeMonth.includes(val.value),
                          ) ?? []
                        }
                        labelledBy={t('MY_TODO.FILTER.SELECT_MONTH')}
                        overrideStrings={{
                          selectSomeItems: t('MY_TODO.FILTER.SELECT_MONTH'),
                        }}
                        onChange={(selected: Option[]) =>
                          handleFilterSelect(
                            selected.map((v) => v.value),
                            'activeMonth',
                          )
                        }
                      />
                    </FormGroup>
                  </Col>
                  {checkArray(dynamicFields) &&
                    dynamicFields.map((ele) => {
                      if (ele.type !== 'dropdown' || !ele.isActive) return null;
                      const brandIds = Array.isArray(activeBrand) ? activeBrand : [];
                      const productTypeIds = Array.isArray(activeProductType) ? activeProductType : [];
                      const matchesBrand = ele.brand?.some((br) => brandIds.includes(br)) ?? false;
                      const matchesProductType = ele.productType?.some((pt) => productTypeIds.includes(pt)) ?? false;
                      const isEnabled =
                        brandIds.length > 0 && productTypeIds.length > 0
                          ? matchesBrand && matchesProductType
                          : true;
                      const selectOpt = createOptions(ele.values ?? [], 'keyName', '_id') as Option[];
                      return (
                        <Col md={2} key={ele._id}>
                          <FormGroup>
                            <MultiSelect
                              options={selectOpt.filter((v) => (v as unknown as { isActive?: boolean }).isActive !== false)}
                              disabled={!isEnabled}
                              className="product-custom-multiselect common-new-multiselect w-100"
                              value={
                                isEnabled
                                  ? selectOpt.filter((v) =>
                                      dynamicTypes.some((d) => d.valId === (v as unknown as { _id?: string })._id),
                                    )
                                  : []
                              }
                              labelledBy={`Select ${ele.keyName}`}
                              overrideStrings={{ selectSomeItems: `Select ${ele.keyName}` }}
                              onChange={(selected: Option[]) =>
                                handleFilterDynamicSelect(
                                  selected.map((v) => (v as unknown as { _id?: string })._id ?? v.value),
                                  ele._id,
                                )
                              }
                            />
                          </FormGroup>
                        </Col>
                      );
                    })}
                  {checkArray(multiDropdownFields) &&
                    multiDropdownFields.map((ele) => {
                      if (!ele.isActive) return null;
                      const productTypeIds = Array.isArray(activeProductType) ? activeProductType : [];
                      const matchesProductType = ele.productType?.length
                        ? ele.productType.some((pt) => productTypeIds.includes(pt))
                        : true;
                      const isEnabled = productTypeIds.length > 0 ? matchesProductType : true;
                      const selectOpt = createOptions(ele.values ?? [], 'keyName', '_id') as Option[];
                      return (
                        <Col md={2} key={ele._id}>
                          <FormGroup>
                            <MultiSelect
                              options={selectOpt.filter((v) => (v as unknown as { isActive?: boolean }).isActive !== false)}
                              disabled={!isEnabled}
                              className="product-custom-multiselect common-new-multiselect w-100"
                              value={
                                isEnabled
                                  ? selectOpt.filter((v) =>
                                      dynamicTypes.some((d) => d.valId === (v as unknown as { _id?: string })._id),
                                    )
                                  : []
                              }
                              labelledBy={`Select ${ele.keyName}`}
                              overrideStrings={{ selectSomeItems: `Select ${ele.keyName}` }}
                              onChange={(selected: Option[]) =>
                                handleFilterDynamicSelect(
                                  selected.map((v) => (v as unknown as { _id?: string })._id ?? v.value),
                                  ele._id,
                                )
                              }
                            />
                          </FormGroup>
                        </Col>
                      );
                    })}
                  <Col md={2}>
                    <FormGroup>
                      <MultiSelect
                        options={TextCustomAttributeOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={
                          TextCustomAttributeOptions?.filter(
                            (val) =>
                              checkArray(TextCustomAttributeOptions) &&
                              textCustomAttribute.includes(val.value),
                          ) ?? []
                        }
                        labelledBy={t('MY_TODO.FILTER.SELECT_TEXT_ATTRIBUTES')}
                        overrideStrings={{ selectSomeItems: t('MY_TODO.FILTER.SELECT_TEXT_ATTRIBUTES') }}
                        onChange={(selected: Option[]) =>
                          handleFilterSelect(selected.map((v) => v.value), 'textCustomAttribute')
                        }
                      />
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <MultiSelect
                        options={productStatusOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={
                          productStatusOptions?.filter(
                            (val) =>
                              checkArray(productStatusOptions) &&
                              productStatus.includes(val.value),
                          ) ?? []
                        }
                        labelledBy={t('MY_TODO.FILTER.SELECT_PRODUCT_STATUS')}
                        overrideStrings={{
                          selectSomeItems: t('MY_TODO.FILTER.SELECT_PRODUCT_STATUS'),
                        }}
                        onChange={(selected: Option[]) =>
                          handleFilterSelect(
                            selected.map((v) => v.value),
                            'productStatus',
                          )
                        }
                      />
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <MultiSelect
                        options={createdByOption}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={
                          createdByOption?.filter(
                            (val) =>
                              checkArray(createdByOption) &&
                              activeCreatedBy.includes(val.value),
                          ) ?? []
                        }
                        labelledBy={t('MY_TODO.FILTER.SELECT_CREATED_BY')}
                        overrideStrings={{
                          selectSomeItems: t('MY_TODO.FILTER.SELECT_CREATED_BY'),
                        }}
                        onChange={(selected: Option[]) =>
                          handleFilterSelect(selected.map((v) => v.value), 'activeCreatedBy')
                        }
                      />
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <MultiSelect
                        options={licensorOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={
                          licensorOptions?.filter(
                            (val) =>
                              checkArray(licensorOptions) &&
                              activeLicensor.includes(val.value),
                          ) ?? []
                        }
                        labelledBy={t('MY_TODO.FILTER.SELECT_LICENSOR')}
                        overrideStrings={{ selectSomeItems: t('MY_TODO.FILTER.SELECT_LICENSOR') }}
                        onChange={(selected: Option[]) =>
                          handleFilterLicense(
                            selected.map((v) => v.value),
                            'activeLicensor',
                          )
                        }
                      />
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <MultiSelect
                        options={licenseOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={
                          licenseOptions?.filter(
                            (val) =>
                              checkArray(licenseOptions) &&
                              activeLicense.includes(val.value),
                          ) ?? []
                        }
                        labelledBy={t('MY_TODO.FILTER.SELECT_LICENSE')}
                        overrideStrings={{ selectSomeItems: t('MY_TODO.FILTER.SELECT_LICENSE') }}
                        onChange={(selected: Option[]) =>
                          handleFilterLicense(
                            selected.map((v) => v.value),
                            'activeLicense',
                          )
                        }
                      />
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <MultiSelect
                        options={likenessOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={
                          likenessOptions?.filter(
                            (val) =>
                              checkArray(likenessOptions) &&
                              activeLikeness.includes(val.value),
                          ) ?? []
                        }
                        labelledBy={t('MY_TODO.FILTER.SELECT_LIKENESS')}
                        overrideStrings={{ selectSomeItems: t('MY_TODO.FILTER.SELECT_LIKENESS') }}
                        onChange={(selected: Option[]) =>
                          handleFilterLicense(
                            selected.map((v) => v.value),
                            'activeLikeness',
                          )
                        }
                      />
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <MultiSelect
                        options={colorwayOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={
                          colorwayOptions?.filter(
                            (val) =>
                              checkArray(colorwayOptions) &&
                              activeColorway.includes(val.value),
                          ) ?? []
                        }
                        labelledBy={t('MY_TODO.FILTER.SELECT_COLOURWAY')}
                        overrideStrings={{
                          selectSomeItems: t('MY_TODO.FILTER.SELECT_COLOURWAY'),
                        }}
                        onChange={(selected: Option[]) =>
                          handleFilterSelect(
                            selected.map((v) => v.value),
                            'activeColorway',
                          )
                        }
                      />
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <MultiSelect
                        options={colorGroupOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={
                          colorGroupOptions?.filter(
                            (val) =>
                              checkArray(colorGroupOptions) &&
                              activeColorGroup.includes(val.value),
                          ) ?? []
                        }
                        labelledBy={t('MY_TODO.FILTER.SELECT_COLOR_GROUP')}
                        overrideStrings={{
                          selectSomeItems: t('MY_TODO.FILTER.SELECT_COLOR_GROUP'),
                        }}
                        onChange={(selected: Option[]) =>
                          handleFilterSelect(
                            selected.map((v) => v.value),
                            'activeColorGroup',
                          )
                        }
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <div className="d-flex justify-content-end">
                      <CustomTooltip placement="top" tooltipText={t('MY_TODO.FILTER.CLEAR')}>
                        <button
                          onClick={() => handleFilterClear()}
                          aria-label="Clear"
                          className="btn p-0 d-inline-flex"
                          type="button"
                        >
                          <span className="icon-Clear-New f-24" />
                        </button>
                      </CustomTooltip>
                      <CustomTooltip placement="top" tooltipText={t('MY_TODO.FILTER.APPLY')}>
                        <button
                          onClick={() => handleFilterApply()}
                          aria-label="Apply"
                          className="btn p-0 d-inline-flex ml-16"
                          type="button"
                        >
                          <span className="icon-Tick-1 f-28" />
                        </button>
                      </CustomTooltip>
                    </div>
                  </Col>
                </Row>
              </Popover.Content>
            </Popover>
          }
        >
          <i className="icon-Filter f-26" />
        </OverlayTrigger>
      </button>
    </CustomTooltip>
  );
};

export default MyTodoFilter;